// Runtime config loader. Each app fetches a single JSON document at boot so
// the same compiled bundle can target dev / test / prod by swapping config.json.
//
// The shape is generic — apps describe their own DynamicConfig and pass it as
// the type parameter. Returns a singleton: subsequent calls return the cached
// value and are cheap.

let _config: unknown;

/** Common fields every MyStratum app's config.json carries. Apps extend this. */
export interface BaseDynamicConfig {
  /** Base URL for API calls. Dev usually `/_api` (Vite proxy). */
  apiUrl: string;
  /** `tms` | `depot` | `expenses`. Sent on every request as `X-App-Key`. */
  appKey: string;
  /** Free-form environment label, e.g. `DEV`, `TEST`, `PROD`. */
  environment: string;
  /** Build version surfaced by the app (release health, support, etc.). */
  version: string;
  /** Auth0 tenant domain. */
  a0Domain: string;
  /** Auth0 SPA application client id. */
  a0ClientId: string;
  /** Auth0 API audience for token requests. */
  a0Audience: string;
  /**
   * Google Maps Platform JS API key. Optional at the type level — apps
   * without map / geocoding screens can omit it — but required in any app
   * that mounts `PlacesAutocomplete` or other `@my/ui` map widgets.
   */
  googleMapsApiKey?: string;
  /**
   * Azure Maps subscription key. Optional — only the TMS manifest map (and
   * later, truck route planning/optimization) uses it. Address geocoding still
   * runs on Google via `googleMapsApiKey`.
   */
  azureMapsKey?: string;
}

/**
 * Fetch and cache `/config.json`. Call once at app boot before rendering.
 * Subsequent calls resolve from the cache.
 *
 * @param url Optional override (defaults to `/config.json`).
 */
export async function loadConfig<T extends BaseDynamicConfig = BaseDynamicConfig>(
  url = '/config.json',
): Promise<T> {
  if (_config) return _config as T;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to fetch ${url}: ${res.status} ${res.statusText}`);
  }
  _config = (await res.json()) as T;
  return _config as T;
}

/**
 * Synchronous accessor. Throws if `loadConfig` hasn't resolved yet — guard at
 * the boot site so the rest of the app can read freely.
 */
export function getConfig<T extends BaseDynamicConfig = BaseDynamicConfig>(): T {
  if (!_config) {
    throw new Error('Config has not been loaded yet. Call loadConfig() before getConfig().');
  }
  return _config as T;
}
